import { estimateResultsSpreadsheetFileRuns } from '@/features/quality/sections/resultsFileSpreadsheetValidation';
import {
  isCsvResultsFile,
  isJsonResultsFile,
  isXmlResultsFile,
} from '@/features/quality/sections/resultsFileUploadUtils';
import type { CsvHeaderAliases, CsvStatusAliases } from '@flowqa/shared';

export type ResultsFileRunEstimate = {
  executionRuns: number;
  suiteRuns: number;
  testRuns: number;
};

type EstimateResultsFileRunsOptions = {
  headerAliases: CsvHeaderAliases;
  statusAliases: CsvStatusAliases;
};

type JsonRecord = Record<string, unknown>;

export const emptyResultsFileRunEstimate = (): ResultsFileRunEstimate => ({
  executionRuns: 0,
  suiteRuns: 0,
  testRuns: 0,
});

export const addResultsFileRunEstimates = (
  left: ResultsFileRunEstimate,
  right: ResultsFileRunEstimate,
): ResultsFileRunEstimate => ({
  executionRuns: left.executionRuns + right.executionRuns,
  suiteRuns: left.suiteRuns + right.suiteRuns,
  testRuns: left.testRuns + right.testRuns,
});

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const countJsonTests = (value: unknown): number => {
  if (Array.isArray(value)) {
    return asArray(value).reduce<number>((total, item) => total + countJsonTests(item), 0);
  }
  if (!isRecord(value)) return 0;

  const directTests = asArray(value.tests).filter((item) => isRecord(item)).length;
  const specTests = asArray(value.specs).reduce<number>((total, spec) => {
    if (!isRecord(spec)) return total;
    return total + asArray(spec.tests).filter((item) => isRecord(item)).length;
  }, 0);
  const nestedSuiteTests = asArray(value.suites).reduce<number>(
    (total, suite) => total + countJsonTests(suite),
    0,
  );
  return directTests + specTests + nestedSuiteTests;
};

const countMochaSuites = (suites: unknown[]): number =>
  suites.reduce<number>((total, suite) => {
    if (!isRecord(suite)) return total;
    const directTests = asArray(suite.tests).filter((item) => isRecord(item)).length;
    const currentSuite = directTests > 0 || typeof suite.title === 'string' ? 1 : 0;
    return total + currentSuite + countMochaSuites(asArray(suite.suites));
  }, 0);

const estimateJsonFileRuns = async (file: File): Promise<ResultsFileRunEstimate | null> => {
  try {
    const parsed = JSON.parse(await file.text()) as unknown;
    if (!isRecord(parsed)) return null;
    const rootSuites = asArray(parsed.suites);
    const rootTests = asArray(parsed.tests).filter((item) => isRecord(item)).length;
    const hasPlaywrightShape = rootSuites.some(
      (suite) => isRecord(suite) && (Array.isArray(suite.specs) || typeof suite.file === 'string'),
    );
    const suiteRuns = hasPlaywrightShape
      ? rootSuites.filter((suite) => countJsonTests(suite) > 0).length
      : Math.max(countMochaSuites(rootSuites), rootTests > 0 ? 1 : 0);
    const testRuns = countJsonTests(parsed);
    if (testRuns === 0) return null;
    return { executionRuns: 1, suiteRuns: Math.max(suiteRuns, 1), testRuns };
  } catch {
    return null;
  }
};

const estimateXmlFileRuns = async (file: File): Promise<ResultsFileRunEstimate | null> => {
  try {
    const content = await file.text();
    const testRuns = content.match(/<testcase(?:\s|>|\/)/gi)?.length ?? 0;
    if (testRuns === 0) return null;
    const suiteRuns = content.match(/<testsuite(?:\s|>|\/)/gi)?.length ?? 0;
    return { executionRuns: 1, suiteRuns: Math.max(suiteRuns, 1), testRuns };
  } catch {
    return null;
  }
};

export const estimateResultsFileRuns = async (
  file: File,
  options: EstimateResultsFileRunsOptions,
): Promise<ResultsFileRunEstimate | null> => {
  if (isCsvResultsFile(file)) {
    const spreadsheetEstimate = await estimateResultsSpreadsheetFileRuns(file, options);
    if (!spreadsheetEstimate) return null;
    return {
      executionRuns: 1,
      suiteRuns: spreadsheetEstimate.suiteRuns,
      testRuns: spreadsheetEstimate.testRuns,
    };
  }
  if (isJsonResultsFile(file)) return estimateJsonFileRuns(file);
  if (isXmlResultsFile(file)) return estimateXmlFileRuns(file);
  return null;
};
